// This file is now a compatibility layer that delegates to the Zustand store
import { usePlanStore, type PackageTier, type ShowWithPackage } from "@/stores/plan-store";
import { type ShowWithDetails } from "@shared/schema";

// Re-export types for backward compatibility
export type { PackageTier, ShowWithPackage };

// Export the PlanStorage object that delegates to Zustand
export const PlanStorage = {
  getSelectedShows: (userId?: number) => usePlanStore.getState().getShows(userId),
  setSelectedShows: (shows: ShowWithDetails[], userId?: number) => {
    // Not directly supported - use addShow/removeShow instead
    console.warn('[PlanStorage] setSelectedShows is deprecated, use addShow/removeShow');
  },
  getShowPackages: (userId?: number) => usePlanStore.getState().getPackages(userId),
  setShowPackage: (showIds: number[], tier: PackageTier, price?: number, userId?: number) =>
    usePlanStore.getState().setPackage(showIds, tier, price, userId),
  addShow: (show: ShowWithDetails, userId?: number, packageTier?: PackageTier, packagePrice?: number) =>
    usePlanStore.getState().addShow(show, userId, packageTier, packagePrice),
  addShowsWithPackage: (shows: ShowWithDetails[], packageTier: PackageTier, packagePrice?: number, userId?: number) =>
    usePlanStore.getState().addShowsWithPackage(shows, packageTier, packagePrice, userId),
  removeShow: (showId: number, userId?: number) =>
    usePlanStore.getState().removeShow(showId, userId),
  clearShows: (userId?: number) =>
    usePlanStore.getState().clearShows(userId),
  getShowCount: (userId?: number) =>
    usePlanStore.getState().getShowCount(userId),
  getShowsWithPackages: (userId?: number) =>
    usePlanStore.getState().getShowsWithPackages(userId),
  getShowsByArtist: (userId?: number) =>
    usePlanStore.getState().getShowsByArtist(userId),
  updateDynamicPricing: async (userId?: number) =>
    await usePlanStore.getState().updateDynamicPricing(userId),
  getTotalCost: (userId?: number) =>
    usePlanStore.getState().getTotalCost(userId),
  isShowInPlan: (showId: number, userId?: number) =>
    usePlanStore.getState().isShowInPlan(showId, userId),
  getShowPackageInfo: (showId: number, userId?: number) =>
    usePlanStore.getState().getShowPackageInfo(showId, userId),
  triggerPricingRecalculation: (userId?: number) => {
    // Not needed with Zustand - updates are automatic
  },
  forceRecalculatePricing: async (userId?: number) =>
    await usePlanStore.getState().updateDynamicPricing(userId),
  debug: (userId?: number) => {
    const state = usePlanStore.getState();
    console.log('🔍 [DEBUG] Plan Storage Debug Info (Zustand):');
    console.log('📦 Shows with packages:', state.getShowsWithPackages(userId));
    console.log('👥 Shows by artist:', state.getShowsByArtist(userId));
    console.log('🏷️ Package storage:', state.getPackages(userId));
    console.log('💰 Total cost:', state.getTotalCost(userId));
    console.log('📊 Show count:', state.getShowCount(userId));
  }
};

// Expose to global for debugging
if (typeof window !== 'undefined') {
  (window as any).PlanStorageDebug = PlanStorage;
}
